import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

import { API_BASE } from '../config/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
      // Sync theme and accent color from user preferences
      if (response.data?.theme) {
        localStorage.setItem('theme', response.data.theme);
        // Apply theme class immediately to document
        const html = document.documentElement;
        html.classList.remove('light', 'dark');
        const resolvedTheme = response.data.theme === 'system' 
          ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
          : response.data.theme;
        html.classList.add(resolvedTheme);
      }
      if (response.data?.accent_color) {
        localStorage.setItem('accentColor', response.data.accent_color);
        // Apply accent class immediately to document
        const html = document.documentElement;
        ['purple', 'blue', 'cyan', 'green', 'orange', 'pink', 'red'].forEach(c => {
          html.classList.remove(`accent-${c}`);
        });
        html.classList.add(`accent-${response.data.accent_color}`);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=') || window.location.pathname === '/auth/callback') {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_BASE}/auth/login`, 
      { email, password },
      { withCredentials: true }
    );
    setUser(response.data);
    // Sync theme and accent color from user preferences
    if (response.data?.theme) {
      localStorage.setItem('theme', response.data.theme);
      // Apply theme class immediately
      const html = document.documentElement;
      html.classList.remove('light', 'dark');
      const resolvedTheme = response.data.theme === 'system' 
        ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
        : response.data.theme;
      html.classList.add(resolvedTheme);
    }
    if (response.data?.accent_color) {
      localStorage.setItem('accentColor', response.data.accent_color);
      // Apply accent class immediately
      const html = document.documentElement;
      ['purple', 'blue', 'cyan', 'green', 'orange', 'pink', 'red'].forEach(c => {
        html.classList.remove(`accent-${c}`);
      });
      html.classList.add(`accent-${response.data.accent_color}`);
    }
    return response.data;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API_BASE}/auth/register`,
      { email, password, name },
      { withCredentials: true }
    );
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API_BASE}/auth/logout`, {}, { withCredentials: true });
    } catch {
      // Ignore errors
    }
    setUser(null);
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const exchangeSession = async (sessionId) => {
    const response = await axios.post(`${API_BASE}/auth/session`,
      { session_id: sessionId },
      { withCredentials: true }
    );
    setUser(response.data);
    // Sync theme from user preferences
    if (response.data?.theme) {
      localStorage.setItem('theme', response.data.theme);
    }
    return response.data;
  };

  const updateProfile = async (profileData) => {
    const response = await axios.put(`${API_BASE}/auth/profile`,
      profileData,
      { withCredentials: true }
    );
    setUser(response.data);
    return response.data;
  };

  const uploadProfilePhoto = async (photoData) => {
    const response = await axios.post(`${API_BASE}/auth/profile/photo`,
      { photo_data: photoData },
      { withCredentials: true }
    );
    // Update user's picture
    setUser(prev => ({ ...prev, picture: response.data.picture }));
    return response.data;
  };

  const changePassword = async (currentPassword, newPassword) => {
    const response = await axios.post(`${API_BASE}/auth/change-password`,
      { current_password: currentPassword, new_password: newPassword },
      { withCredentials: true }
    );
    return response.data;
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    loginWithGoogle,
    exchangeSession,
    checkAuth,
    setUser,
    updateProfile,
    uploadProfilePhoto,
    changePassword
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
